import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { IntlShape } from 'react-intl-next';

import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import Tooltip from '@atlaskit/tooltip';
import Textfield from '@atlaskit/textfield';
import { Label, HelperMessage } from '@atlaskit/form';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { UploadImageResult } from '../link-cards/components/CardsAreaContainerComponents/LinkCardsUnsplashSearch';

const i18n = defineMessages({
	uploadedImageAltText: {
		id: 'custom-sites-extensions.uploaded.image.alt.text',
		defaultMessage: 'Add alt text to uploaded image',
		description:
			'Message when a user has an uploaded image to add alt text in the link cards config panel',
	},
	uploadedImageAltTextInfo: {
		id: 'custom-sites-extensions.uploaded.image.edit.alt.text.info',
		defaultMessage:
			'Alt text is useful for people using screen readers because of visual limitations.',
		description:
			'Message inside tooltip that explains to the user that alt text is useful for people using screen readers',
	},
	uploadedImageAltTextMoreInfoLabel: {
		id: 'custom-sites-extensions.uploaded.image.edit.alt.text.more.info.label',
		defaultMessage: 'More info is provided on hover',
		description:
			'Tooltip icon that provides context to the user that alt text is useful for people using screen readers',
	},
});

const altTextWrapperStyles = xcss({
	marginBottom: 'space.100',
});

const labelWrapperStyles = xcss({
	fontSize: '16px',
});

const helperMessageWrapperStyles = xcss({
	justifyContent: 'space-between',
});

const disabledLabelStyles = xcss({
	color: 'color.text.disabled',
	':hover': {
		cursor: 'not-allowed',
	},
});

type AltTextFieldProps = {
	imageAltText?: string;
	selectedImageUrl?: string;
	handleImageSelection: ({ uploadedImageId, altDescription }: UploadImageResult) => void;
	intl: IntlShape;
	isDisabled?: boolean;
};

export const AltTextField = ({
	imageAltText,
	selectedImageUrl,
	handleImageSelection,
	intl,
	isDisabled,
}: AltTextFieldProps) => {
	const handleUpdateAltText = (event) => {
		const { value } = event?.currentTarget;
		if (!!selectedImageUrl) {
			handleImageSelection({
				uploadedImageId: selectedImageUrl,
				altDescription: value,
			});
		}
	};
	return (
		<Box xcss={altTextWrapperStyles}>
			<Flex xcss={helperMessageWrapperStyles}>
				<Box xcss={labelWrapperStyles}>
					<Label htmlFor="alt-text">
						<HelperMessage>
							{/* eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage */}
							<Flex xcss={isDisabled ? disabledLabelStyles : {}}>
								<FormattedMessage {...i18n.uploadedImageAltText} />
							</Flex>
						</HelperMessage>
					</Label>
				</Box>

				{isDisabled ? (
					<div data-testId="disabled-alt-text">
						<EditorPanelIcon
							label={intl.formatMessage(i18n.uploadedImageAltTextMoreInfoLabel)}
							primaryColor={token('color.icon.disabled')}
						/>
					</div>
				) : (
					<div data-testId="alt-text-tooltip">
						<Tooltip position="top" content={intl.formatMessage(i18n.uploadedImageAltTextInfo)}>
							<EditorPanelIcon
								label={intl.formatMessage(i18n.uploadedImageAltTextMoreInfoLabel)}
								primaryColor={token('color.icon')}
							/>
						</Tooltip>
					</div>
				)}
			</Flex>
			<Textfield
				id="alt-text"
				value={imageAltText}
				onChange={handleUpdateAltText}
				maxLength={100}
				isDisabled={isDisabled}
			/>
		</Box>
	);
};
